import React, { useState } from 'react';
import { Profile, Dispo } from '../types';
import { TimeSlotIcon } from './TimeSlotIcons';
import { DisclaimerModal } from './DisclaimerModal';
// Import ContactInfoModal supprimé car géré au niveau de la page
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { consumeToken, isRateLimited } from '../utils/rateLimit';
import { executeRecaptcha } from '../services/recaptcha';
import { formatAnnee } from '../utils/formatAnnee';

interface TutorCardProps {
  tutor: Profile;
  onRate?: (tutor: Profile) => void;
  onReview?: () => void;
  onViewReviews?: () => void;
  onContactRequest?: (tutorName: string, isSuccess: boolean, message?: string) => void;
  onContactInfo?: (tutorUserId: string) => void; // Nouveau callback pour afficher les infos de contact
}

export const TutorCard: React.FC<TutorCardProps> = ({ 
  tutor, 
  onRate,
  onReview, 
  onViewReviews,
  onContactRequest,
  onContactInfo
}) => {
  const { user } = useAuth();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  // État showContactInfoModal supprimé car géré au niveau de la page
  const getFiliereInitials = (filiere?: string) => {
    if (!filiere) return 'NS';
    const filiereLower = filiere.toLowerCase();
    if (filiereLower.includes('informatique de gestion') || filiereLower.includes('ig')) return 'IG';
    if (filiereLower.includes("economie d'entreprise") || filiereLower.includes('ee')) return 'EE';
    if (filiereLower.includes('international business') || filiereLower.includes('ibm')) return 'IBM';
    if (filiereLower.includes('information science') || filiereLower.includes('is')) return 'IS';
    const words = filiere.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      return words.slice(0, 2).map(w => w.charAt(0).toUpperCase()).join('');
    }
    return words[0].slice(0, 2).toUpperCase();
  };

  /**
   * Retourne des classes tailwind pastel harmonisées avec les badges du filtre dynamique
   * - IS: rose rouge pastel
   * - IG: vert pastel
   * - EE: bleu pastel
   * - IBM: violet pastel
   */
  const getFiliereStyles = (filiere?: string) => {
    const fallback = {
      badge: 'bg-gray-200 text-gray-800',
      chip: 'bg-gray-50 text-gray-700 border-gray-200'
    };
    if (!filiere) return fallback;
    const s = filiere.toLowerCase();
    if (s.includes('information science') || s.includes('is')) {
      return { badge: 'bg-rose-200 text-gray-800', chip: 'bg-rose-50 text-rose-800 border-rose-200' };
    }
    if (s.includes('informatique de gestion') || s.includes('ig')) {
      return { badge: 'bg-green-200 text-gray-800', chip: 'bg-green-50 text-green-800 border-green-200' };
    }
    if (s.includes("economie d'entreprise") || s.includes("économie d'entreprise") || s.includes('ee')) {
      return { badge: 'bg-blue-200 text-gray-800', chip: 'bg-blue-50 text-blue-800 border-blue-200' };
    }
    if (s.includes('international business') || s.includes('ibm')) {
      return { badge: 'bg-purple-200 text-gray-800', chip: 'bg-purple-50 text-purple-800 border-purple-200' };
    }
    return fallback;
  };

  const formatAvailabilitiesTable = (dispos?: Dispo[]) => {
    // Construit une table 7x3 de disponibilité (matin/apm/soir) par jour
    const jours: Array<Dispo['jour']> = ['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'];
    const creneaux: Array<Dispo['creneau']> = ['matin','apm','soir'];

    const availabilityByDay: Record<string, Set<string>> = {};
    if (dispos && dispos.length > 0) {
      dispos.forEach(d => {
        if (!availabilityByDay[d.jour]) availabilityByDay[d.jour] = new Set();
        availabilityByDay[d.jour].add(d.creneau);
      });
    }

    const dayShort: Record<Dispo['jour'], string> = {
      lundi: 'Lun', mardi: 'Mar', mercredi: 'Mer', jeudi: 'Jeu', vendredi: 'Ven', samedi: 'Sam', dimanche: 'Dim'
    };

    const headerCell = (slot: Dispo['creneau']) => (
      <div className="flex items-center justify-center">
        <TimeSlotIcon timeSlot={slot} size={16} />
      </div>
    );

    return (
      <div className="w-full text-xs">
        <div className="grid grid-cols-[56px_repeat(3,_minmax(0,_1fr))] gap-y-1">
          {/* En-tête */}
          <div></div>
          {creneaux.map(slot => (
            <div key={`h-${slot}`} className="text-gray-600 font-medium text-center">
              {headerCell(slot)}
            </div>
          ))}
          {/* Lignes */}
          {jours.map(jour => {
            const set = availabilityByDay[jour] || new Set<string>();
            return (
              <React.Fragment key={jour}>
                <div className="font-medium text-gray-600">{dayShort[jour]}:</div>
                {creneaux.map(slot => (
                  <div key={`${jour}-${slot}`} className="flex items-center justify-center">
                    <span className={`inline-block h-3.5 w-3.5 rounded-full border ${set.has(slot) ? 'bg-green-500 border-green-600' : 'bg-gray-200 border-gray-300'}`}></span>
                  </div>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const calculateAvgRating = () => {
    if (!tutor.notes || tutor.notes.length === 0) {
      return { avgRating: 0, reviewCount: 0 };
    }
    const reviewCount = tutor.notes.length;
    const sum = tutor.notes.reduce((acc, curr) => acc + curr.note, 0);
    const avgRating = sum / reviewCount;
    return { avgRating, reviewCount };
  };

  const handleInstantConnect = async () => {
    // Rate limit rapide côté client
    if (isRateLimited('instant_connect')) {
      if (onContactRequest) {
        onContactRequest(`${tutor.prenom}${tutor.nom ? ' ' + tutor.nom : ''}`, false, 'Trop de tentatives. Veuillez réessayer dans une minute.');
      }
      return;
    }
    consumeToken('instant_connect');

    // Si l'utilisateur n'est pas connecté, afficher le disclaimer
    if (!user) {
      setShowDisclaimerModal(true);
      return;
    }

    // Vérifier si l'utilisateur essaie de se contacter lui-même
    if (user.id === tutor.user_id) {
      if (onContactRequest) {
        onContactRequest(
          `${tutor.prenom}${tutor.nom ? ' ' + tutor.nom : ''}`, 
          true, 
          '🎭 Mode test activé ! Vous consultez votre propre profil. En temps normal, vous ne pourriez pas vous contacter vous-même.'
        );
      }
      return;
    }

    try {
      // reCAPTCHA (optionnel, nécessite une clé site si définie)
      const recaptchaSiteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY as string) || undefined;
      await executeRecaptcha('instant_connect', recaptchaSiteKey);

      // Vérifier d'abord si un match mutuel existe déjà
      const { data: mutualMatch, error: mutualError } = await supabase
        .from('matchs_valides')
        .select('*')
        .eq('etudiant_id', user.id)
        .eq('repetiteur_id', tutor.id)
        .maybeSingle();

      if (mutualMatch && !mutualError) {
        // Ouvrir directement le modal d'informations de contact
        if (onContactInfo) {
          onContactInfo(tutor.user_id);
        }
        return;
      }

      // Vérifier si l'étudiant a déjà liké ce tuteur
      const { data: existingMatch, error: checkError } = await supabase
        .from('matchs')
        .select('like_etudiant, like_tuteur')
        .eq('etudiant_id', user.id)
        .eq('repetiteur_id', tutor.id)
        .maybeSingle();

      if (existingMatch && existingMatch.like_etudiant && !checkError) {
        if (existingMatch.like_tuteur) {
          // Match mutuel - ouvrir le modal d'informations de contact
          if (onContactInfo) {
            onContactInfo(tutor.user_id);
          }
        } else {
          // Like déjà envoyé mais pas de réponse du tuteur
          if (onContactRequest) {
            onContactRequest(
              `${tutor.prenom}${tutor.nom ? ' ' + tutor.nom : ''}`,
              true,
              'Votre intérêt a déjà été manifesté ! Attendez que le tuteur ou la tutrice vous réponde.'
            );
          }
        }
        return;
      }

      // Créer ou mettre à jour le match avec le like de l'étudiant
      const { error } = await supabase
        .from('matchs')
        .upsert({
          etudiant_id: user.id,
          repetiteur_id: tutor.id,
          like_etudiant: true
        }, { 
          onConflict: 'etudiant_id,repetiteur_id' 
        })
        .select();

      if (error) {
        if (onContactRequest) {
          onContactRequest(
            `${tutor.prenom}${tutor.nom ? ' ' + tutor.nom : ''}`,
            false,
            'Erreur lors de l\'envoi du like. Veuillez réessayer.'
          );
        }
      } else {
        if (onContactRequest) {
          onContactRequest(
            `${tutor.prenom}${tutor.nom ? ' ' + tutor.nom : ''}`,
            true,
            '🎓 Intérêt manifesté ! Le tuteur ou la tutrice sera notifié-e et pourra manifester son intérêt en retour pour révéler vos informations de contact.'
          );
        }
      }
    } catch (err) {
      if (onContactRequest) {
        onContactRequest(
          `${tutor.prenom}${tutor.nom ? ' ' + tutor.nom : ''}`,
          false,
          'Une erreur inattendue s\'est produite.'
        );
      }
    }
  };

  const { avgRating, reviewCount } = calculateAvgRating();
  const initials = getFiliereInitials(tutor.filiere);
  const filiereStyles = getFiliereStyles(tutor.filiere);
  const isOwnProfile = user && user.id === tutor.user_id;

  return (
    <div className="tutor-card relative">
      {/* Badge "Mon profil" si c'est son propre profil */}
      {isOwnProfile && (
        <div className="absolute top-2 right-2 z-10">
          <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">
            <i className="fas fa-user-check mr-1"></i>
            Mon profil
          </div>
        </div>
      )}
      
      <div className="p-4 flex flex-col h-full">
        {/* Contenu principal avec flex-grow pour pousser le bouton vers le bas */}
        <div className="flex-grow">
          <div className="text-center mb-3">
            {/* Nom en premier, plus gros */}
            <h3 className="text-xl font-bold mb-2 font-heading text-gray-900">
              {tutor.prenom}{tutor.nom ? ` ${tutor.nom}` : ''}
            </h3>
            
            {/* Degré d'étude et badge filière */}
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-gray-600 text-sm font-medium">
                {formatAnnee(tutor.annee)}
              </span>
              <span className={`${filiereStyles.badge} px-2 py-1 rounded-full text-xs font-semibold`}>
                {initials}
              </span>
            </div>
            
            {/* Note centrée (toujours affichée) */}
            <div className="flex items-center justify-center gap-1 mb-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <i
                    key={i}
                    className={`fas fa-star ${avgRating > 0 && i <= Math.round(avgRating) ? 'text-yellow-400' : 'text-gray-300'} text-sm`}
                  ></i>
                ))}
              </div>
              {avgRating > 0 ? (
                <span className="text-gray-600 text-xs ml-1">({reviewCount} avis)</span>
              ) : (
                <span className="text-gray-500 text-xs ml-1">(Nouveau tuteur)</span>
              )}
            </div>

            {/* Prix centré sous les notes */}
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center gap-2">
                <i className="fas fa-coins text-yellow-500 text-lg"></i>
                <span className="text-2xl font-bold text-gray-800">{tutor.tarif || 0}</span>
                <span className="text-sm text-gray-600 font-medium">CHF/h</span>
              </div>
            </div>
          </div>

          {/* Matières (gauche) + Disponibilités (droite) */}
          <div className="grid grid-cols-2 gap-3 items-start">
            {/* Colonne gauche: Matières (toujours affichée même si vide) */}
            <div>
              <div className="bg-gray-50 p-1.5 rounded-md min-h-[80px]">
                {(() => {
                  const matieresList = (tutor.matieres || '')
                    .split(',')
                    .map(m => m.trim())
                    .filter(m => m);
                  if (matieresList.length === 0) {
                    return (
                      <div className="h-full flex items-center justify-center">
                        <p className="text-gray-400 text-xs text-center">Matières non renseignées</p>
                      </div>
                    );
                  }
                  const maxToShow = 6;
                  const shown = matieresList.slice(0, maxToShow);
                  const remaining = matieresList.length - shown.length;
                  return (
                    <div className="flex flex-wrap gap-1.5">
                      {shown.map((m, idx) => (
                        <span
                          key={`${m}-${idx}`}
                          className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs border ${filiereStyles.chip}`}
                        >
                          {m}
                        </span>
                      ))}
                      {remaining > 0 && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] sm:text-xs bg-blue-50 text-blue-700 border border-blue-200">+{remaining}</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Colonne droite: Disponibilités en tableau (toujours affiché) */}
            <div>
              <div className="bg-gray-50 p-1.5 rounded-md">
                {formatAvailabilitiesTable(tutor.dispos)}
              </div>
            </div>
          </div>
        </div>

        {/* Bouton Instant Connect - Toujours en bas avec espacement fixe */}
        <div className="mt-4 -mx-4 -mb-4">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleInstantConnect();
            }}
            className={`w-full py-3 text-white rounded-b-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.01] border-t ${
              isOwnProfile 
                ? 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 border-yellow-400/20' 
                : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 border-green-400/20'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              {isOwnProfile ? (
                <>
                  <i className="fas fa-eye text-yellow-200"></i>
                  <span className="text-sm font-medium">Mode test</span>
                  <i className="fas fa-vial text-yellow-200"></i>
                </>
              ) : (
                <>
                  <i className="fas fa-bolt text-yellow-300 animate-pulse"></i> 
                  <span className="text-sm font-medium">Instant Connect</span>
                  <div className="w-1.5 h-1.5 bg-yellow-300 rounded-full animate-ping"></div>
                </>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Modals */}
      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        tutorName={`${tutor.prenom}${tutor.nom ? ' ' + tutor.nom : ''}`}
        onLoginSuccess={() => {
          setShowDisclaimerModal(false);
          // Après connexion réussie, relancer l'action instant connect
          handleInstantConnect();
        }}
      />

      {/* ContactInfoModal supprimé - géré au niveau de la page */}
    </div>
  );
}; 